import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** Escalas disponíveis para a logo na sidebar. */
export type EscalaLogo = 1 | 2 | 3;
export const ESCALAS_LOGO: EscalaLogo[] = [1, 2, 3];

interface SidebarContextValue {
  /** Sidebar retraída (modo só-ícones) no desktop. */
  colapsada: boolean;
  alternar: () => void;
  definir: (valor: boolean) => void;
  /** Escala da logo na sidebar (1x, 2x, 3x). */
  escalaLogo: EscalaLogo;
  definirEscalaLogo: (valor: EscalaLogo) => void;
  cicloEscalaLogo: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

const CHAVE_STORAGE = 'edulumina:sidebar:colapsada';
const CHAVE_ESCALA = 'edulumina:sidebar:escala-logo';

const lerEscala = (): EscalaLogo => {
  if (typeof window === 'undefined') return 1;
  try {
    const raw = window.localStorage.getItem(CHAVE_ESCALA);
    const num = Number(raw);
    return (ESCALAS_LOGO as number[]).includes(num) ? (num as EscalaLogo) : 1;
  } catch {
    return 1;
  }
};

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [colapsada, setColapsada] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(CHAVE_STORAGE) === '1';
    } catch {
      return false;
    }
  });

  const [escalaLogo, setEscalaLogo] = useState<EscalaLogo>(() => lerEscala());

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_STORAGE, colapsada ? '1' : '0');
    } catch {
      /* ignora indisponibilidade de storage */
    }
  }, [colapsada]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_ESCALA, String(escalaLogo));
    } catch {
      /* ignora */
    }
  }, [escalaLogo]);

  const alternar = useCallback(() => setColapsada((v) => !v), []);
  const definir = useCallback((valor: boolean) => setColapsada(valor), []);
  const definirEscalaLogo = useCallback((valor: EscalaLogo) => setEscalaLogo(valor), []);
  const cicloEscalaLogo = useCallback(
    () =>
      setEscalaLogo((atual) => {
        const idx = ESCALAS_LOGO.indexOf(atual);
        return ESCALAS_LOGO[(idx + 1) % ESCALAS_LOGO.length];
      }),
    [],
  );

  const value = useMemo<SidebarContextValue>(
    () => ({ colapsada, alternar, definir, escalaLogo, definirEscalaLogo, cicloEscalaLogo }),
    [colapsada, alternar, definir, escalaLogo, definirEscalaLogo, cicloEscalaLogo],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = (): SidebarContextValue => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar deve ser usado dentro de <SidebarProvider>.');
  }
  return ctx;
};
